import { v4 as uuid } from 'uuid';
import { IInlineBoxData, InlineBoxType } from '$/components/InlineBox/types';
import type { EditorSerializedContent } from '$/components/InputTextArea/types';
import { AgentInfo, TeamAgentSkillInfo } from '$/pages/RoboneoTeamMessageList/types';

export const INLINE_BOX_REG = /\[@((?:input_)?(?:image|video|file|skill|agent)):(?:#(\d+):)?([^\]]+)\]/g;
const PREFIX_INPUT_INLINE_BOX = '[@input_';
const PREFIX_AGENT_INLINE_BOX = '[@agent:';
// 通过文本是否包含[@input_或[@agent:前缀判断是否是输入框序列化格式
export const isInputInLineBox = (text: string) => {
  return text.includes(PREFIX_INPUT_INLINE_BOX) || text.includes(PREFIX_AGENT_INLINE_BOX);
};

export type TextAreaNodeType = InlineBoxType.Text | InlineBoxType.Mark | InlineBoxType.Image | InlineBoxType.Video | InlineBoxType.File | InlineBoxType.Skill | 'agent';

// 输入内容格式解析
export function parseTextToInlineBox({
  content,
  markElementList,
  agentList,
}:
{ content: string;
  markElementList?: IInlineBoxData[];
  agentList?: AgentInfo[]; }) {
  // const { content, markElementList, agentList } = message;

  const markMap = new Map(
    markElementList?.map((item) => [item.index, item]) || [],
  );

  const result: ({ type: TextAreaNodeType;
    content?: string;
    data?: IInlineBoxData | AgentInfo; })[] = [];
  let lastIndex = 0;

  content.replace(INLINE_BOX_REG, (match, mediaType, order, markIndex, offset) => {
    // 普通文本
    if (offset > lastIndex) {
      result.push({
        type: InlineBoxType.Text,
        content: content.slice(lastIndex, offset),
      });
    }

    // image 占位符
    const mark = markMap.get(markIndex);
    if (mark) {
      let type = InlineBoxType.Text;
      if (!isInputInLineBox(match)) {
        type = InlineBoxType.Mark;
      } else if (mark.mediaType === 'image') {
        type = InlineBoxType.Image;
      } else if (mark.mediaType === 'video') {
        type = InlineBoxType.Video;
      } else if (mark.mediaType === 'file') {
        type = InlineBoxType.File;
      } else if (mark.mediaType === 'skill') {
        type = InlineBoxType.Skill;
      }
      result.push({
        data: {
          ...mark,
          order: order ? Number(order) : 0,
        },
        type,
      });
    } else if (mediaType === 'agent' && agentList) {
      // agent 占位符
      const agent = agentList.find((item) => `${item.id}` === `${markIndex}`);
      if (agent) {
        result.push({
          type: 'agent',
          data: agent,
        });
      }
    } else {
      // 兜底：找不到 mark和agent 占位符的情况
      result.push({
        type: InlineBoxType.Text,
        content: match,
      });
    }

    lastIndex = offset + match.length;
    return match;
  });

  // 结尾文本
  if (lastIndex < content.length) {
    result.push({
      type: InlineBoxType.Text,
      content: content.slice(lastIndex),
    });
  }

  return result;
}

/**
 * 将 popupData 中的 text + markElementList + agentInfoList 解析为 EditorSerializedContent，
 * 用于预填编辑器（isAutoSubmit = false 场景）。
 */
export function parsePopupDataToEditorContent(data: {
  text?: string;
  markElementList?: IInlineBoxData[];
  agentInfoList?: Record<string, any>[];
}): EditorSerializedContent {
  const editorContent: EditorSerializedContent = [];
  const markList = data.markElementList ?? [];
  const markMap = new Map(markList.map((item) => [item.index, item]));

  // 1. agent mentions
  if (data.agentInfoList?.length) {
    data.agentInfoList.forEach((item: any) => {
      editorContent.push({ type: 'mention', value: { id: item.id, name: item.name } });
    });
  }

  // 2. 解析 text 中的内联标记，按顺序还原媒体位置
  const sourceText = data.text || '';
  const usedIndices = new Set<string>();
  let lastIdx = 0;
  INLINE_BOX_REG.lastIndex = 0;
  let m = INLINE_BOX_REG.exec(sourceText);
  while (m !== null) {
    if (m.index > lastIdx) {
      editorContent.push({ type: 'text', value: sourceText.slice(lastIdx, m.index) });
    }
    const [, tagType, , markIndex] = m;
    const mark = markMap.get(markIndex);
    if (mark && tagType.includes('input_')) {
      usedIndices.add(markIndex);
      let boxType = InlineBoxType.File;
      if (mark.mediaType === 'image') boxType = InlineBoxType.Image;
      else if (mark.mediaType === 'video') boxType = InlineBoxType.Video;
      else if (mark.mediaType === 'skill') boxType = InlineBoxType.Skill;
      editorContent.push({
        type: 'inline-box',
        value: { type: boxType, data: mark },
      });
    }
    // 跳过 agent 标记（已通过 agentInfoList 处理）
    lastIdx = m.index + m[0].length;
    m = INLINE_BOX_REG.exec(sourceText);
  }
  if (lastIdx < sourceText.length) {
    editorContent.push({ type: 'text', value: sourceText.slice(lastIdx) });
  }

  return editorContent;
}

export interface BuildPopupDataParams {
  room_id: string;
  text: string;
  /** 已整合的媒体列表，优先级高于 images/videos/documents */
  markElementList?: IInlineBoxData[];
  /** 分离的图片列表，仅在 markElementList 未传时使用 */
  images?: IInlineBoxData[];
  /** 分离的视频列表，仅在 markElementList 未传时使用 */
  videos?: IInlineBoxData[];
  /** 分离的文件列表，仅在 markElementList 未传时使用 */
  documents?: IInlineBoxData[];
  usedModel?: any;
  activeMode?: string;
  agentInfoList?: Record<string, any>[];
  skillList?: TeamAgentSkillInfo[];
  agentThinkingModeType?: string;
  isAutoSubmit?: boolean;
  /** 是否把未在 text 中引用的资源自动拼到文字前，旧首页同款链路默认保留该兼容行为。 */
  autoPrefixUnreferencedResources?: boolean;
  cardId?: string;
  workFlowData?: any;
  [key: string]: any;
}

/**
 * 补齐 popupData 媒体项字段，保持与 master 同款链路的格式一致。
 */
function normalizeMediaItem(item: any, defaultMediaType: string, defaultName: string): IInlineBoxData {
  return {
    ...item,
    index: item.index ? `${item.index}` : uuid(),
    mediaType: item.mediaType || defaultMediaType,
    source: item.source || 'input',
    name: item.name || defaultName,
  };
}

/**
 * 构建标准化 popupData，统一写入格式。
 * - 媒体统一整合到 markElementList（不再输出 images/videos/documents 分离字段）
 * - text 带内联标记（[@agent:id] + [@input_image:idx] + ... + 原始文本）
 * - originalText 保留不含标记的纯文本，用于预填编辑器解析位置
 */
export function buildPopupData(params: BuildPopupDataParams): Record<string, any> {
  const {
    room_id, text, markElementList: inputMarkList,
    images, videos, documents,
    agentInfoList,
    autoPrefixUnreferencedResources = true,
    ...rest
  } = params;

  // 1. 确定 markElementList 来源
  let markElementList: IInlineBoxData[];
  if (inputMarkList && inputMarkList.length > 0) {
    // 已整合的 markElementList，仅补全缺失字段
    markElementList = inputMarkList.map((item) => {
      let defaultName = 'file';
      if (item.mediaType === 'image') defaultName = 'image';
      else if (item.mediaType === 'video') defaultName = 'video';
      return normalizeMediaItem(item, item.mediaType || 'image', defaultName);
    });
  } else {
    // 从分离的 images/videos/documents 合并，和 master 的一键同款取值保持一致。
    const normalizedImages = (images || []).map((item) => normalizeMediaItem(item, 'image', 'image'));
    const normalizedVideos = (videos || []).map((item) => normalizeMediaItem(item, 'video', 'video'));
    const normalizedDocuments = (documents || []).map((item) => normalizeMediaItem(item, 'file', 'file'));
    markElementList = [...normalizedImages, ...normalizedVideos, ...normalizedDocuments];
  }

  // 2. 构建 text，旧链路默认把未引用媒体拼到文字前，新首页可关闭此兼容拼接。
  let finalText: string;
  if (isInputInLineBox(text)) {
    finalText = text;
  } else {
    let tagPrefix = '';
    if (agentInfoList?.length) {
      tagPrefix += agentInfoList.map((item) => `[@agent:${item.id}]`).join('');
    }
    if (autoPrefixUnreferencedResources) {
      markElementList.forEach((item) => {
        let typeTag = 'file';
        if (item.mediaType === 'image') typeTag = 'image';
        else if (item.mediaType === 'video') typeTag = 'video';
        else if (item.mediaType === 'skill') typeTag = 'skill';
        tagPrefix += `[@input_${typeTag}:${item.index}]`;
      });
    }
    finalText = tagPrefix + text;
  }

  return {
    room_id,
    text: finalText,
    markElementList,
    images: markElementList.filter((item) => item.mediaType === 'image'),
    videos: markElementList.filter((item) => item.mediaType === 'video'),
    documents: markElementList.filter((item) => item.mediaType === 'file'),
    agentInfoList,
    autoPrefixUnreferencedResources,
    ...rest,
  };
}

// 3.0上线之前处理普通agent和agent team的兼容
// function normalizeMediaItem-old(item: any, defaultMediaType: string, defaultName: string): IInlineBoxData {
//   return {
//     ...item,
//     index: `${item.index}` || uuid(),
//     mediaType: item.mediaType || defaultMediaType,
//     source: item.source || 'input',
//     name: item.name || defaultName,
//   };
// }

// /**
//  * 构建标准化 popupData，统一写入格式。
//  * - 媒体统一整合到 markElementList（不再输出 images/videos/documents 分离字段）
//  * - text 带内联标记（[@agent:id] + [@input_image:idx] + ... + 原始文本）
//  * - originalText 保留不含标记的纯文本，用于预填编辑器解析位置
//  */
// 3.0上线之前处理普通agent和agent team的兼容
// export function buildPopupData-old(params: BuildPopupDataParams): Record<string, any> {
//   const {
//     room_id, text, markElementList: inputMarkList,
//     images, videos, documents,
//     agentInfoList,
//     ...rest
//   } = params;

//   // 1. 确定 markElementList 来源
//   let markElementList: IInlineBoxData[];
//   if (inputMarkList && inputMarkList.length > 0) {
//     // 已整合的 markElementList，仅补全缺失字段
//     markElementList = inputMarkList.map((item) => {
//       let defaultName = 'file';
//       if (item.mediaType === 'image') defaultName = 'image';
//       else if (item.mediaType === 'video') defaultName = 'video';
//       return normalizeMediaItem(item, item.mediaType || 'image', defaultName);
//     });
//   } else {
//     // 从分离的 images/videos/documents 合并
//     const normalizedImages = (images || []).map((item) => normalizeMediaItem(item, 'image', 'image'));
//     const normalizedVideos = (videos || []).map((item) => normalizeMediaItem(item, 'video', 'video'));
//     const normalizedDocuments = (documents || []).map((item) => normalizeMediaItem(item, 'file', 'file'));
//     markElementList = [...normalizedImages, ...normalizedVideos, ...normalizedDocuments];
//   }

//   // 2. 构建 text + originalText
//   let finalText: string;

//   if (isInputInLineBox(text)) {
//     // 新格式：text 已含 [@input_xxx] 标记
//     finalText = text;
//   } else {
//     let tagPrefix = '';
//     if (agentInfoList?.length) {
//       tagPrefix += agentInfoList.map((item) => `[@agent:${item.id}]`).join('');
//     }
//     markElementList.forEach((item) => {
//       let typeTag = 'file';
//       if (item.mediaType === 'image') typeTag = 'image';
//       else if (item.mediaType === 'video') typeTag = 'video';
//       else if (item.mediaType === 'skill') typeTag = 'skill';
//       tagPrefix += `[@input_${typeTag}:${item.index}]`;
//     });
//     finalText = tagPrefix + text;
//   }

//   return {
//     room_id,
//     text: finalText,
//     markElementList,
//     // 向后兼容：旧读取端仍读 images/videos/documents
//     images: markElementList.filter((i) => i.mediaType === 'image'),
//     videos: markElementList.filter((i) => i.mediaType === 'video'),
//     documents: markElementList.filter((i) => i.mediaType === 'file'),
//     agentInfoList,
//     ...rest,
//   };
// }
